import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobAPI, userHideAPI } from '../services/api';
import './UserActivitySettings.css';

const UserActivitySettings = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    type: 'all',
    date_from: '',
    date_to: '',
  });
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [unhidingJobId, setUnhidingJobId] = useState(null);
  const [unhidingUserId, setUnhidingUserId] = useState(null);

  // Activity type labels
  const activityTypeLabels = {
    job_report: 'Job Report',
    job_unhide: 'Job Unhide',
    user_report: 'User Report',
    user_hide: 'User Hide',
    user_unhide: 'User Unhide',
    job_application: 'Job Application',
    account_deactivation: 'Account Deactivation',
    account_reactivation: 'Account Reactivation',
  };

  useEffect(() => {
    fetchActivities();
  }, [currentPage, filters]);

  const fetchActivities = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: currentPage,
        per_page: 10,
        ...filters,
      };

      // Remove 'all' values and empty strings from params
      Object.keys(params).forEach(key => {
        if (params[key] === 'all' || params[key] === '') {
          delete params[key];
        }
      });

      const response = await jobAPI.getMyActivities(params);
      if (response.success) {
        const activitiesData = response.data?.data || response.data || [];
        setActivities(activitiesData);
        setPagination(response.data);
      } else {
        setError(response.message || 'Failed to load activities');
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError('Failed to load activities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnhide = async (jobId, e) => {
    if (e) {
      e.stopPropagation();
    }
    setUnhidingJobId(jobId);
    try {
      const response = await jobAPI.unhide(jobId);
      if (response.success) {
        // Refresh activities to get updated state
        fetchActivities();
      } else {
        alert(response.message || 'Failed to unhide job');
      }
    } catch (err) {
      console.error('Error unhiding job:', err);
      alert('Failed to unhide job. Please try again.');
    } finally {
      setUnhidingJobId(null);
    }
  };

  const handleUnhideUser = async (userId, e) => {
    if (e) {
      e.stopPropagation();
    }
    setUnhidingUserId(userId);
    try {
      const response = await userHideAPI.unhide(userId);
      if (response.success) {
        // Refresh activities to get updated state
        fetchActivities();
      } else {
        alert(response.message || 'Failed to unhide user');
      }
    } catch (err) {
      console.error('Error unhiding user:', err);
      alert('Failed to unhide user. Please try again.');
    } finally {
      setUnhidingUserId(null);
    }
  };

  const handleViewLink = (link) => {
    if (link) {
      // Always open in a new tab
      if (link.startsWith('http://') || link.startsWith('https://')) {
        // Full URL, open directly in new tab
        window.open(link, '_blank', 'noopener,noreferrer');
      } else {
        // Relative path, convert to full URL and open in new tab
        const fullUrl = window.location.origin + (link.startsWith('/') ? link : '/' + link);
        window.open(fullUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActivityTypeLabel = (type) => {
    return activityTypeLabels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'job_report':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="12" y1="18" x2="12" y2="12"></line>
            <line x1="9" y1="15" x2="15" y2="15"></line>
          </svg>
        );
      case 'job_unhide':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        );
      case 'user_report':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        );
      case 'user_hide':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
            <line x1="1" y1="1" x2="23" y2="23"></line>
          </svg>
        );
      case 'user_unhide':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        );
      case 'job_application':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
        );
      case 'account_deactivation':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18"></path>
            <path d="M6 6l12 12"></path>
            <circle cx="12" cy="12" r="10"></circle>
          </svg>
        );
      case 'account_reactivation':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        );
    }
  };

  return (
    <div className="user-activity-settings-container">
      <div className="user-activity-settings-header">
        <h2>User Activity</h2>
        <p>View and manage your activities</p>
      </div>

      <div className="user-activity-settings-content">
        {/* Filters */}
        <div className="activity-filters">
          <div className="filter-group">
            <label htmlFor="type-filter">Activity Type:</label>
            <select
              id="type-filter"
              value={filters.type}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, type: e.target.value }));
                setCurrentPage(1);
              }}
            >
              <option value="all">All Types</option>
              {Object.entries(activityTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="date-from-filter">From Date:</label>
            <input
              id="date-from-filter"
              type="date"
              value={filters.date_from}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, date_from: e.target.value }));
                setCurrentPage(1);
              }}
              className="date-filter-input"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="date-to-filter">To Date:</label>
            <input
              id="date-to-filter"
              type="date"
              value={filters.date_to}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, date_to: e.target.value }));
                setCurrentPage(1);
              }}
              className="date-filter-input"
              min={filters.date_from || undefined}
            />
          </div>
          {(filters.date_from || filters.date_to) && (
            <div className="filter-group filter-group-button">
              <label>&nbsp;</label>
              <button
                type="button"
                onClick={() => {
                  setFilters(prev => ({ ...prev, date_from: '', date_to: '' }));
                  setCurrentPage(1);
                }}
                className="clear-date-filter-btn"
              >
                Clear Dates
              </button>
            </div>
          )}
        </div>

        {/* Activities List */}
        {loading ? (
          <div className="activity-loading">
            <div className="activity-spinner"></div>
            <span>Loading activities...</span>
          </div>
        ) : error ? (
          <div className="activity-error">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <p>{error}</p>
            <button onClick={fetchActivities} className="retry-btn">Retry</button>
          </div>
        ) : activities.length === 0 ? (
          <div className="activity-empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <h3>No Activities Found</h3>
            <p>You haven't performed any activities yet.</p>
          </div>
        ) : (
          <>
            <div className="activity-reports-list">
              {activities.map((activity) => {
                const metadata = activity.metadata || {};
                const jobId = metadata.job_id;
                const userId = metadata.hidden_user_id;
                const isJobUnhide = activity.type === 'job_unhide';
                const isJobReport = activity.type === 'job_report';
                const isUserHide = activity.type === 'user_hide';
                const isUserUnhide = activity.type === 'user_unhide';
                
                return (
                  <div key={activity.id} className="activity-report-item">
                    <div className="report-item-header">
                      <div className="report-job-info">
                        <div className="activity-type-header">
                          <span className="activity-icon">{getActivityIcon(activity.type)}</span>
                          <h3 className="report-job-title">{activity.title}</h3>
                        </div>
                        <div className="report-job-meta">
                          <span className="activity-type-badge">{getActivityTypeLabel(activity.type)}</span>
                          <span className="report-separator">•</span>
                          <span className="report-date">{formatDate(activity.created_at)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="report-item-body">
                      <div className="report-details">
                        {activity.description && (
                          <div className="report-detail-row">
                            <span className="report-value">{activity.description}</span>
                          </div>
                        )}
                        {isJobReport && metadata.reason_label && (
                          <div className="report-detail-row">
                            <span className="report-label">Reason:</span>
                            <span className="report-value">{metadata.reason_label}</span>
                          </div>
                        )}
                        {isJobReport && metadata.status && (
                          <div className="report-detail-row">
                            <span className="report-label">Status:</span>
                            <span className="report-value">{metadata.status}</span>
                          </div>
                        )}
                      </div>

                      <div className="report-actions">
                        {activity.link && (
                          <button
                            className="btn-view-job"
                            onClick={() => handleViewLink(activity.link)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                              <polyline points="15 3 21 3 21 9"></polyline>
                              <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                            View Related
                          </button>
                        )}
                        {isJobUnhide && jobId && (
                          <button
                            className="btn-unhide-job"
                            onClick={(e) => handleUnhide(jobId, e)}
                            disabled={unhidingJobId === jobId}
                          >
                            {unhidingJobId === jobId ? (
                              <>
                                <div className="spinner-small"></div>
                                Processing...
                              </>
                            ) : (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                  <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                                View Job
                              </>
                            )}
                          </button>
                        )}
                        {isUserUnhide && userId && activity.link && (
                          <button
                            className="btn-unhide-job"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewLink(activity.link);
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                            View User
                          </button>
                        )}
                        {isUserHide && userId && activity.link && (
                          <button
                            className="btn-unhide-job"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewLink(activity.link);
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                              <line x1="1" y1="1" x2="23" y2="23"></line>
                            </svg>
                            View User
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination && pagination.last_page > 1 && (
              <div className="activity-pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1 || loading}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {currentPage} of {pagination.last_page}
                  {pagination.total !== undefined && (
                    <span className="pagination-total"> ({pagination.total} total)</span>
                  )}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(pagination.last_page, prev + 1))}
                  disabled={currentPage === pagination.last_page || loading}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserActivitySettings;
